/**
 * The Grade 3 Tigard Times Tables curriculum: units, stages, keypad routes, anchors.
 * These are the locked teaching decisions from the Oct 28, 2026 handoff. Change them here, nowhere else.
 */

export const SETTINGS = {
  /** Warm-up counts 1×n … 10×n and back. */
  COUNT_TO: 10,
  /** Forward + back, this many times for today's table. */
  WARM_ROUNDS: 2,
  /** Review facts per earlier table in the warm-up. */
  REVIEW_PER: 3,
  /** Cap on review facts in the warm-up. */
  REVIEW_MAX: 12,
  /** Problems per worksheet. */
  SHEET: 12,
  /** Of SHEET correct on the first try to pass a stage. */
  PASS: 10,
  /** The fluency goal. Measured, never enforced. */
  FLUENT_MS: 3000,
} as const;

export interface Unit {
  id: string;
  name: string;
  /** Tables practiced in this unit. */
  tables: number[];
  /** The 11th-and-12th-facts unit: only ×11 and ×12 of its tables. */
  special?: boolean;
  /** Division units: "how many 9s make 27?" — the answer is how many times you counted. */
  op?: "div";
  /** Mixed division: every divisor learned so far. */
  mixed?: boolean;
}

/** Grade 3 assumes the 1s, 2s, 5s and 10s are already taught. The program opens with the 9s. */
export const UNITS: readonly Unit[] = [
  { id: "t9", name: "The 9s", tables: [9] },
  { id: "t1112", name: "The 11th and 12th facts", tables: [2, 5, 9], special: true },
  { id: "t8", name: "The 8s", tables: [8] },
  { id: "t7", name: "The 7s", tables: [7] },
  { id: "t3", name: "The 3s", tables: [3] },
  { id: "t4", name: "The 4s", tables: [4] },
  { id: "t6", name: "The 6s", tables: [6] },
  { id: "t11", name: "The 11s", tables: [11] },
  { id: "t12", name: "The 12s", tables: [12] },
  // Division, one table at a time in the order the tables were taught, then mixed (Meliesa, Oct 29).
  { id: "d9", name: "Divide by 9", tables: [9], op: "div" },
  { id: "d8", name: "Divide by 8", tables: [8], op: "div" },
  { id: "d7", name: "Divide by 7", tables: [7], op: "div" },
  { id: "d3", name: "Divide by 3", tables: [3], op: "div" },
  { id: "d4", name: "Divide by 4", tables: [4], op: "div" },
  { id: "d6", name: "Divide by 6", tables: [6], op: "div" },
  { id: "d12", name: "Divide by 12", tables: [12], op: "div" },
  { id: "dmix", name: "Mixed division", tables: [9, 8, 7, 3, 4, 6, 12], op: "div", mixed: true },
];

export const isDiv = (u: Unit): boolean => u.op === "div";

/** Review in the warm-up starts here (never the 1s, 2s, 5s, 10s). */
export const REVIEW_FROM = "t9";

export type GuideKind = "full" | "start";

export interface Stage {
  name: string;
  desc: string;
  /** Live keypad on screen. */
  kp?: boolean;
  /** Which guide to draw on the keypad. */
  guide?: GuideKind;
  /** Show the nearest-anchor cue with each problem. */
  anchorCue?: boolean;
  /** Student builds the keypad from memory first. */
  build?: boolean;
  /** "Show my keypad" / "Show the anchor" buttons available. */
  tools?: boolean;
  /** Timed against the 3-second goal; only here does a fact count as fluent. */
  timed?: boolean;
}

export const PATTERN_STAGES: readonly Stage[] = [
  { name: "Solve on the keypad", desc: "The keypad shows the direction of counting and the puzzle: the anchor, the arrows, and the answer key. Count from the anchor, then type the answer.", kp: true, guide: "full" },
  { name: "Count from the nearest anchor", desc: "The keypad shows the puzzle only: the anchor and the arrows to the answer key. The cue names the anchor.", kp: true, guide: "start", anchorCue: true },
  { name: "Build your keypad, tools when needed", desc: "Draw your keypad on paper, then build it here from memory. The anchor and arrows stay put away until you ask for them.", build: true, tools: true },
  { name: "No tools", desc: "No keypad, no anchor. Everything is learned; now it's headed for mastery. Your goal is each fact in under 3 seconds. The ones that take longer come back for review.", timed: true },
];

export const ELEVEN_STAGES: readonly Stage[] = [
  { name: "Practice", desc: "The 11th fact is an anchor: your number written twice. Get the 12th fact by counting forward from the 11th. The 9s keypad is on screen; the 2s and 5s are counted without one.", kp: true, guide: "start", anchorCue: true },
  { name: "No tools", desc: "Headed for mastery: your goal is each fact in under 3 seconds. The ones that take longer come back for review.", timed: true },
];

/** Division, in the book's order: count from the start first; the anchors come in Stage 2. */
export const DIV_STAGES: readonly Stage[] = [
  { name: "Count to the number", desc: "How many 9s make 27? Dial the code and count by 9s until you reach the number. Every key you touch is one count — the answer is how many times you counted, not the number you landed on.", kp: true, guide: "start" },
  { name: "Jump to the nearest anchor", desc: "You know the anchors. 45 is the 5th. Count the short way from the nearest one — up or down — and the answer is the count.", kp: true, guide: "start", anchorCue: true },
  { name: "Build your keypad, tools when needed", desc: "Draw your keypad on paper, then build it here from memory. The anchors stay put away until you ask.", build: true, tools: true },
  { name: "No tools", desc: "Headed for mastery: your goal is each fact in under 3 seconds. The ones that take longer come back for review.", timed: true },
];

export const REVIEW_STAGE: Stage = { name: "Mixed review", desc: "Every unit so far, no tools. Facts you missed before come back here.", timed: true };

export const stagesFor = (u: Unit): readonly Stage[] => (u.special ? ELEVEN_STAGES : u.op === "div" ? DIV_STAGES : PATTERN_STAGES);

/**
 * Scaffolds come down across LESSONS. A table is practiced at Stage 1 in its own lesson; each later lesson
 * reviews it one stage lighter: 1 lesson later → Stage 2 (anchor), 2 later → Stage 3 (build, tools when needed),
 * 3 or more later → Stage 4 (no tools). Returns the stage index for reviewing `table` during `currentUnitId`.
 */
export function reviewStageIndex(table: number, currentUnitId: string): number {
  const intro = UNITS.findIndex((u) => !u.special && !u.op && u.tables.includes(table));
  const cur = UNITS.findIndex((u) => u.id === currentUnitId);
  if (intro < 0 || cur <= intro) return 0;
  return Math.min(PATTERN_STAGES.length - 1, cur - intro);
}

/** The next unit opens once today's table passes Stage 1 (the special unit: its first stage). */
export const STAGES_TO_UNLOCK_NEXT = 1;

/**
 * Direction of counting on the keypad, as runs of keys. Solid arrows within a run, a dashed jump between runs, then 0.
 * The 11s have no route: ×11 is "your number written twice." The 12s count like the 2s (12 ends with a 2):
 * 2, 4, 6, 8, skip a line, 0 — and the code repeats, so the middle anchor (60) tells which half. (Meliesa, Oct 28.)
 */
export const ROUTES: Readonly<Record<number, readonly (readonly number[])[]>> = {
  9: [[9, 8, 7], [6, 5, 4], [3, 2, 1], [0]],
  8: [[8, 6, 4, 2], [0]], // even keys only, counting down by 2s; the code repeats, so the middle anchor (40) tells which half
  7: [[7, 4, 1], [8, 5, 2], [9, 6, 3], [0]],
  3: [[3, 6, 9], [2, 5, 8], [1, 4, 7], [0]],
  6: [[6], [2, 8], [4], [0]],
  4: [[4], [8, 2], [6], [0]],
  12: [[2, 4, 6, 8], [0]], // twelve ends with a 2, so it counts like a 2; useful for division later
};

/** The first key of a table's route — where counting starts (the 12s start on the 2). */
export const routeStart = (table: number): number => ROUTES[table]?.[0]?.[0] ?? table;

/** Standard 10-key layout, reading order. The 0 sits in its own box below. */
export const KEYPAD_LAYOUT = [7, 8, 9, 4, 5, 6, 1, 2, 3] as const;

/** Even tables show only the even keys; the odd spaces are blank. Odd tables show all ten. */
export const keyShows = (table: number, digit: number): boolean => (table % 2 === 1 ? true : digit % 2 === 0);

/** ×1, ×5, ×10 for every table; ×11 is also an anchor for single-digit tables ("your number written twice"). */
export const anchorsFor = (table: number): number[] => (table <= 9 ? [1, 5, 10, 11] : [1, 5, 10]);

export type Op = "mul" | "div";
/** "9×4" for a times fact; "9÷4" for the division fact 36 ÷ 9 = 4 (table first, count second, both ways). */
export const factKey = (a: number, b: number, op: Op = "mul"): string => `${a}${op === "div" ? "÷" : "×"}${b}`;
export const parseKey = (k: string): [number, number] => {
  const [a, b] = k.split(/[×÷]/).map(Number);
  return [a ?? 0, b ?? 0];
};
export const keyOp = (k: string): Op => (k.includes("÷") ? "div" : "mul");

export const unitById = (id: string): Unit => UNITS.find((u) => u.id === id) ?? UNITS[0]!;
export const unitIndex = (id: string): number => UNITS.findIndex((u) => u.id === id);
